import { Megaphone, Music2, Save } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { embedProviderLabels } from "@vzlive/shared";
import { apiFetch } from "../../lib/api";

interface AlertState {
  text: string;
  tone: "info" | "warning" | "critical";
  active: boolean;
}

interface StickyState {
  embedUrl: string;
  title: string;
  active: boolean;
}

const emptyAlert: AlertState = { text: "", tone: "info", active: true };
const emptySticky: StickyState = { embedUrl: "", title: "", active: true };

export function EventAlertPanels({ eventId }: { eventId: string }) {
  const [alert, setAlert] = useState<AlertState>(emptyAlert);
  const [sticky, setSticky] = useState<StickyState>(emptySticky);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setSaved("");
    setError("");
    let cancelled = false;
    void (async () => {
      const [alertResult, stickyResult] = await Promise.allSettled([
        apiFetch<AlertState>(`/api/admin/events/${eventId}/alert`),
        apiFetch<StickyState>(`/api/admin/events/${eventId}/sticky-media`),
      ]);
      if (cancelled) return;
      setAlert(
        alertResult.status === "fulfilled" ? alertResult.value : emptyAlert,
      );
      setSticky(
        stickyResult.status === "fulfilled" ? stickyResult.value : emptySticky,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const flash = (message: string) => {
    setSaved(message);
    window.setTimeout(() => setSaved(""), 2_200);
  };

  const submitAlert = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError("");
    try {
      await apiFetch(`/api/admin/events/${eventId}/alert`, {
        method: "PUT",
        body: JSON.stringify({
          text: form.get("text"),
          tone: form.get("tone"),
          active: form.get("active") === "on",
        }),
      });
      flash("Alert-Banner aktualisiert");
    } catch {
      setError("Alert-Banner konnte nicht gespeichert werden.");
    }
  };

  const submitSticky = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError("");
    try {
      await apiFetch(`/api/admin/events/${eventId}/sticky-media`, {
        method: "PUT",
        body: JSON.stringify({
          embedUrl: form.get("embedUrl"),
          title: form.get("title"),
          active: form.get("active") === "on",
        }),
      });
      flash("Sticky Player aktualisiert");
    } catch {
      setError("Sticky Player konnte nicht gespeichert werden.");
    }
  };

  return (
    <div className="settings-grid">
      {error && (
        <p role="alert" className="settings-panel-error">
          {error}
        </p>
      )}
      {saved && <p role="status">{saved}</p>}

      <section className="settings-panel">
        <header>
          <h2>Alert-Banner</h2>
          <Megaphone />
        </header>
        <p>
          Zeigt einen sofort sichtbaren Hinweis über der Timeline dieses Events.
        </p>
        <form
          key={`${eventId}-alert`}
          onSubmit={submitAlert}
          className="settings-form"
        >
          <label className="wide">
            <span>Text</span>
            <textarea
              name="text"
              rows={3}
              maxLength={500}
              required
              defaultValue={alert.text}
            />
          </label>
          <label>
            <span>Ton</span>
            <select name="tone" defaultValue={alert.tone}>
              <option value="info">Info</option>
              <option value="warning">Warnung</option>
              <option value="critical">Kritisch</option>
            </select>
          </label>
          <label className="switch-row">
            <span>Aktiv</span>
            <input
              name="active"
              type="checkbox"
              role="switch"
              defaultChecked={alert.active}
            />
          </label>
          <button type="submit">
            <Save size={17} /> Banner speichern
          </button>
        </form>
      </section>

      <section className="settings-panel">
        <header>
          <h2>Sticky Player</h2>
          <Music2 />
        </header>
        <p>
          Bindet einen externen Audio- oder Video-Player per Embed ein und
          verankert ihn am unteren Reader-Rand. Der Player lädt erst nach einem
          Tap. Aktuell unterstützt: {embedProviderLabels.join(", ")} – weitere
          Anbieter erscheinen als Link.
        </p>
        <form
          key={`${eventId}-sticky`}
          onSubmit={submitSticky}
          className="settings-form"
        >
          <label className="wide">
            <span>Player-URL</span>
            <input
              name="embedUrl"
              type="url"
              required
              maxLength={2000}
              placeholder="https://mixlr.com/users/12345/embed"
              defaultValue={sticky.embedUrl}
            />
          </label>
          <label className="wide">
            <span>Titel</span>
            <input
              name="title"
              required
              maxLength={180}
              placeholder="Live-Stream"
              defaultValue={sticky.title}
            />
          </label>
          <label className="switch-row wide">
            <span>Im Reader aktiv</span>
            <input
              name="active"
              type="checkbox"
              role="switch"
              defaultChecked={sticky.active}
            />
          </label>
          <button type="submit">
            <Save size={17} /> Player anheften
          </button>
        </form>
      </section>
    </div>
  );
}
